/**
 * <s-card-deck> — Stacked card deck for mobile.
 *
 * Commands (from buttons via commandfor/command):
 *   --next  flip the front card away and bring the next one forward (loops)
 *
 * Required markup: direct children marked [data-deck-card].
 * Writes [data-deck-pos] (0 = front) on each card; CSS only stacks them
 * below 640px, so on wider screens the attribute has no visual effect.
 */
class SCardDeck extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });
    this.#cards().forEach((card, index) => card.setAttribute('data-deck-pos', String(index)));
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #cards() {
    return [...this.querySelectorAll(':scope > [data-deck-card]')];
  }

  #handleCommand = (event) => {
    if (event.command !== '--next' || this.#busy) return;
    const cards = this.#cards();
    if (cards.length < 2) return;
    const front = cards.find((card) => card.dataset.deckPos === '0');
    if (!front) return;

    this.#busy = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    front.setAttribute('data-deck-leaving', '');

    // Bring every other card one step forward while the front card flips away.
    for (const card of cards) {
      if (card === front) continue;
      card.setAttribute('data-deck-pos', String(Number(card.dataset.deckPos) - 1));
    }

    window.setTimeout(() => {
      // Drop the flipped card to the back without animating it, then let it fade in behind.
      front.setAttribute('data-deck-reset', '');
      front.removeAttribute('data-deck-leaving');
      front.setAttribute('data-deck-pos', String(cards.length - 1));
      void front.offsetWidth;
      front.removeAttribute('data-deck-reset');
      this.#busy = false;
    }, reduce ? 200 : 480);
  };
}

if (!customElements.get('s-card-deck')) customElements.define('s-card-deck', SCardDeck);
